import {
  AdvancedFilter,
  FilterOperation,
  FilterEvent,
  SearchEvent,
  FilterName,
  TableConfig,
  FieldType,
  FormatType,
  ColumnSorterInfo,
} from "src/app/models/custom-table/custom-table.model";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import {
  PropertyDTO,
  DataTypeCode,
} from "src/app/models/Properties/propertyDTO";

export function setCategories(categories: CategoryDTO[]): TableConfig[] {
  return categories
    .filter((category) => category.enabled && category.categoryElements)
    .map((category) => {
      return {
        header: category.description,
        field: `categories.${category.code}`,
        fieldType: FieldType.STRING,
        hidden: !category.mandatory,
      };
    });
}

export function setProperties(properties: PropertyDTO[]): TableConfig[] {
  return properties
    .filter((property) => property.enabled)
    .map((property) => {
      return {
        header: property.description,
        field: `properties.${property.code}`,
        fieldType: setPropertyFieldType(property.dataTypeCode),
        formatType:
          property.dataTypeCode === "DATE" ? FormatType.DATETIME : null,
        hidden: !property.mandatory,
      };
    });
}

export function setColumnInfoCategoriesProperties(
  columnInfo: ColumnSorterInfo[],
  tableConfig: TableConfig[]
): ColumnSorterInfo[] {
  const treatedColumnInfo = tableConfig.reduce((acc, cur) => {
    const { field, hidden, header } = cur;
    const foundColInfo = columnInfo.find(({ id }) => id === field);
    if (foundColInfo) {
      return [...acc, foundColInfo]; 
    } else {
      return [
        ...acc, 
        { 
          hidden: hidden, 
          pin: false, 
          name: header, 
          id: field, 
        },
      ];
    }
  }, []);

  return treatedColumnInfo.sort((a, b) => {
    if (columnInfo.indexOf(a) === -1) {
      return 1;
    }
    return columnInfo.indexOf(a) - columnInfo.indexOf(b)
  });
}

export function setSimpleFilterQuery(filteringArgs: SearchEvent): string {
  const { filterConfig, searchTerms } = filteringArgs;

  return searchTerms.reduce(
    (accQuery: string, curSearchTerm: string): string => {
      filterConfig.forEach(({ field }, index: number) => {
        let expression = `${field} ${FilterOperation.CONTAINS} '%${curSearchTerm}%'`;
        accQuery += accQuery.length
          ? index === 0
            ? ` ${FilterOperation.AND} (${expression}`
            : ` ${FilterOperation.OR} ${expression}`
          : `(${expression}`;
      });
      return `${accQuery})`;
    },
    ""
  );
}

export function setAdvancedFilterQuery(
  advancedFilteringArgs: FilterEvent
): string {
  const { advancedFilter } = advancedFilteringArgs;

  // Group filters by column
  const grouped = advancedFilter.reduce((acc, filter) => {
    if (!acc[filter.column]) {
      acc[filter.column] = [];
    }
    acc[filter.column].push(filter);
    return acc;
  }, {} as Record<string, AdvancedFilter[]>);

  // Build the query parts
  const queryParts: string[] = Object.values(grouped).map(filters => {
    const expressions = filters.map(f =>
      `${f.column} ${buildFilterExpression(f.operator, f.value)}`
    );

    // Determine logic: AND for DATE/DATE_TIME, OR for others
    const useAnd = filters[0].fieldType === FieldType.DATE || filters[0].fieldType === FieldType.DATE_TIME;

    if (expressions.length > 1) {
      return `(${expressions.join(` ${useAnd ? FilterOperation.AND : FilterOperation.OR} `)})`;
    }
    
    return expressions[0];
  });

  return queryParts.join(` ${FilterOperation.AND} `);
}

export function setAdvancedFilterQueryBillable(
  advancedFilteringArgs: FilterEvent
): string {
  const { advancedFilter } = advancedFilteringArgs;

  const conditions: { [key: string]: string[] } = {};

  advancedFilter.forEach(({ column, operator, value }: AdvancedFilter) => {
    const condition = `${column} ${buildFilterExpression(operator, value)}`;
    conditions[column] = conditions[column] || [];
    conditions[column].push(condition);
  });

  const queries = Object.entries(conditions).map(([column, columnConditions]) => {
    const conditionStr = columnConditions.length === 1
      ? columnConditions[0]
      : `(${columnConditions.join(` ${column === 'documentDate' ? FilterOperation.AND : FilterOperation.OR} `)})`;
    return conditionStr;
  });

  return queries.join(` ${FilterOperation.AND} `);
}

export function removeTableConfigOptions(
  tableConfig: TableConfig[]
): TableConfig[] {
  return tableConfig.filter((config) => config.fieldType !== FieldType.OPTIONS);
}

export function removeColumnSorterInfoOptions(
  columnInfo: ColumnSorterInfo[]
): ColumnSorterInfo[] {
  return columnInfo.filter((config) => config.id !== FieldType.OPTIONS);
}

export function setAdvancedFilter(
  filterToAdd: AdvancedFilter,
  filterEvent: FilterEvent
): FilterEvent {
  return filterEvent && filterEvent.advancedFilter
    ? {
        advancedFilter: [...filterEvent.advancedFilter, filterToAdd],
      }
    : { advancedFilter: [filterToAdd] };
}

function buildFilterExpression(
  operator: FilterName,
  value: string | number | boolean
): string {
  switch (operator) {
    case FilterName.CONTAINS:
      return `${FilterOperation.CONTAINS} '%${value}%'`;
    case FilterName.EQUALS:
      return `${FilterOperation.EQUALS} '${value}'`;
    case FilterName.STARTS_WITH:
      return `${FilterOperation.CONTAINS} '${value}%'`;
    case FilterName.ENDS_WITH:
      return `${FilterOperation.CONTAINS} '%${value}'`;
    case FilterName.EQUALS_TO:
      return `${FilterOperation.EQUALS} '${value}'`;
    case FilterName.EQUAL:
      return `${FilterOperation.EQUALS} ${value}`;
    case FilterName.NOT_EQUAL:
      return `${FilterOperation.NOT_EQUAL} ${value}`;
    case FilterName.GREATER_THAN:
      return `${FilterOperation.GREATER_THAN} ${value}`;
    case FilterName.LESS_THAN:
      return `${FilterOperation.LESS_THAN} ${value}`;
    case FilterName.GREATER_THAN_EQUAL:
      return `${FilterOperation.GREATER_THAN_EQUAL} ${value}`;
    case FilterName.LESS_THAN_EQUAL:
      return `${FilterOperation.LESS_THAN_EQUAL} ${value}`;
    default:
      break;
  }
}

function setPropertyFieldType(dataTypeCode: string): FieldType {
  switch (dataTypeCode) {
    case DataTypeCode.TEXT:
      return FieldType.STRING;
    case DataTypeCode.NUMERIC:
      return FieldType.NUMBER;
    case DataTypeCode.DATE:
      return FieldType.DATE;
    case DataTypeCode.BOOLEAN:
      return FieldType.BOOLEAN;
    default:
      break;
  }
}
